import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { bookmarkStep } from '@/api/onboarding'
import {
  activateConsent,
  activateIntro,
  CONSENT_VERSION,
  getShowcase,
  ShowcaseApiError,
  ShowcaseStatus,
} from '@/api/showcase'
import { track, trackError } from '@/lib/analytics'

/**
 * Onboarding шаг — согласие на публикацию (слайд 16 нового дизайна).
 *
 * Phase 1 — видна только из BranchPage под whitelist (см. useShowcaseAccess).
 * После «Дальше» вызывает activateIntro (если нужно) + activateConsent и
 * переходит на /onboarding/showcase-rules.
 *
 * Если бэк отдаст 409 «уже в этом состоянии» (юзер прошёл шаг раньше) —
 * молча идём дальше, не блокируем UX.
 */
export function ShowcaseConsentPage() {
  const navigate = useNavigate()
  const { selectedChannel } = useChannel()
  const [chk1, setChk1] = useState(false)
  const [chk2, setChk2] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [initialStatus, setInitialStatus] = useState<ShowcaseStatus | null>(null)

  const canContinue = chk1 && chk2 && !!selectedChannel?.uuid

  // Подгружаем текущий статус Витрины. Если уже active/choosing/processing —
  // показываем хинт «уже включено», но кнопка работает (бэк сам разрулит).
  useEffect(() => {
    if (!selectedChannel?.uuid) return
    getShowcase(selectedChannel.uuid)
      .then((s) => setInitialStatus(s.status))
      .catch(() => setInitialStatus(null))
  }, [selectedChannel?.uuid])

  async function handleNext() {
    if (!selectedChannel?.uuid) return
    setSubmitting(true)
    setError(null)
    try {
      // intro → consent — обе идемпотентные на бэке. 409 ловим в обоих,
      // потому что юзер мог пройти эти шаги раньше через /showcase.
      try {
        await activateIntro(selectedChannel.uuid)
      } catch (e) {
        if (!(e instanceof ShowcaseApiError && e.status === 409)) throw e
      }
      try {
        await activateConsent(selectedChannel.uuid, {
          consentVersion: CONSENT_VERSION,
          checkboxes: [chk1, chk2, true],
        })
      } catch (e) {
        if (!(e instanceof ShowcaseApiError && e.status === 409)) throw e
      }
      track('showcase_onboarding_consent', {
        channelUUID: selectedChannel.uuid,
        message: 'согласие принято в онбординге',
      })
      bookmarkStep('showcase-rules', 'showcase')
      navigate('/onboarding/showcase-rules')
    } catch (e) {
      trackError(e, 'showcase-onboarding-consent')
      setError(e instanceof Error ? e.message : 'Не удалось сохранить согласие')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      step="showcase-consent"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/branch')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button
            className="foot-next"
            disabled={!canContinue || submitting}
            onClick={handleNext}
          >
            {submitting ? 'Сохраняем…' : 'Дальше — что публиковать'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="sc-wrap">
        <div className="sc-left">
          <p className="stage-eyebrow">
            <span className="stage-eyebrow-pill">Бесплатный трафик</span>
            Витрина в&nbsp;поиске
          </p>
          <h1 className="stage-title">
            Пусть посты <em>находят в&nbsp;Google и&nbsp;Яндексе</em>.
          </h1>
          <p className="stage-sub">
            MAX-каналы не&nbsp;видны поисковикам — посты теряются. Мы&nbsp;публикуем их&nbsp;на&nbsp;портале <b>telegraphyx.ru/channels</b>, оптимизируем под&nbsp;поиск, и&nbsp;люди приходят в&nbsp;канал из&nbsp;Google и&nbsp;Яндекса.
          </p>

          <div className="sc-steps">
            <div className="sc-step">
              <span className="sc-step-num">1</span>
              <span className="sc-step-text">
                Мы <b>публикуем</b> ваши посты на&nbsp;портале
              </span>
            </div>
            <div className="sc-step">
              <span className="sc-step-num">2</span>
              <span className="sc-step-text">
                Поисковики их <b>находят</b>{' '}
                <span className="sc-engine"><span className="sc-yandex">Я</span>ндекс</span>{' '}
                <span className="sc-engine">
                  <span style={{ color: '#4285f4' }}>G</span>
                  <span style={{ color: '#ea4335' }}>o</span>
                  <span style={{ color: '#fbbc05' }}>o</span>
                  <span style={{ color: '#4285f4' }}>g</span>
                  <span style={{ color: '#34a853' }}>l</span>
                  <span style={{ color: '#ea4335' }}>e</span>
                </span>
              </span>
            </div>
            <div className="sc-step">
              <span className="sc-step-num sc-step-num-ok">3</span>
              <span className="sc-step-text">
                Люди читают и <b>переходят в&nbsp;канал</b>
              </span>
            </div>
          </div>

          <div className="sc-stats">
            <div>
              <div className="sc-stat-v">84 000</div>
              <div className="sc-stat-l">страниц уже в&nbsp;поиске</div>
            </div>
            <div className="sc-stats-div" />
            <div>
              <div className="sc-stat-v">28 000<span className="sc-stat-period">/мес</span></div>
              <div className="sc-stat-l">читателей из&nbsp;поиска</div>
            </div>
          </div>

          {initialStatus && initialStatus !== 'locked' && initialStatus !== 'intro' && (
            <div className="sc-already">
              Витрина уже подключена к&nbsp;этому каналу (<b>{initialStatus}</b>) — пройдите шаги, чтобы убедиться что всё ок.
            </div>
          )}
        </div>

        <div className="sc-right">
          <div className="sc-eyebrow">Согласие на&nbsp;публикацию</div>
          <p className="sc-right-sub">
            Чтобы публиковать посты на&nbsp;портале, нужно ваше согласие. Это&nbsp;обязательный шаг — потом можно отключить в&nbsp;один клик.
          </p>

          <label className={`sc-check ${chk1 ? 'on' : ''}`}>
            <input type="checkbox" checked={chk1} onChange={(e) => setChk1(e.target.checked)} />
            <span className="sc-check-mark" />
            <span className="sc-check-text">
              Разрешаю публиковать посты канала на&nbsp;портале <b>telegraphyx.ru/channels</b>
            </span>
          </label>
          <label className={`sc-check ${chk2 ? 'on' : ''}`}>
            <input type="checkbox" checked={chk2} onChange={(e) => setChk2(e.target.checked)} />
            <span className="sc-check-mark" />
            <span className="sc-check-text">
              Подтверждаю, что&nbsp;контент мой и&nbsp;не&nbsp;нарушает авторских прав
            </span>
          </label>

          <div className="sc-promise">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            Любой пост можно убрать из&nbsp;поиска, а&nbsp;модуль — выключить целиком
          </div>

          {error && <div className="sc-error">{error}</div>}

          <div className="sc-legal">
            Подробнее —{' '}
            <a href="/legal/showcase" target="_blank" rel="noopener noreferrer" className="sc-legal-link">
              условия публикации
            </a>
            .
          </div>
        </div>
      </div>

      <style>{`
        .sc-wrap {
          display: grid;
          grid-template-columns: 1.15fr 1fr;
          gap: 32px;
          align-items: start;
        }
        @media (max-width: 960px) {
          .sc-wrap { grid-template-columns: 1fr; }
        }
        .sc-steps {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin: 24px 0 22px;
        }
        .sc-step { display: flex; align-items: center; gap: 12px; }
        .sc-step-num {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          color: white;
          font-weight: 700;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sc-step-num-ok { background: var(--tg-up, #00a860); }
        .sc-step-text { font-size: 14px; color: var(--tg-ink, #111); display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .sc-engine {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 2px 9px;
          border-radius: 99px;
          background: var(--tg-surface-2, #f5f5f9);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          font-size: 11px;
          font-weight: 700;
          color: var(--tg-ink-2, #333);
        }
        .sc-yandex { color: #fc3f1d; }
        .sc-stats {
          display: flex;
          gap: 24px;
          padding: 16px 20px;
          background: var(--tg-blue-tint-bg, #f4f8ff);
          border: 1px solid var(--tg-blue-soft, #b9d4ff);
          border-radius: 14px;
        }
        .sc-stats-div { width: 1px; background: var(--tg-blue-soft, #b9d4ff); }
        .sc-stat-v {
          font-weight: 800;
          font-size: 22px;
          letter-spacing: -0.02em;
          color: var(--tg-ink, #111);
        }
        .sc-stat-period { font-size: 13px; color: var(--tg-mute, #5b6678); font-weight: 600; }
        .sc-stat-l { font-size: 12px; color: var(--tg-mute, #5b6678); margin-top: 2px; }
        .sc-already {
          margin-top: 14px;
          padding: 10px 14px;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0052cc);
          border-radius: 10px;
          font-size: 12.5px;
          line-height: 1.5;
        }
        .sc-right {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 18px;
          padding: 24px;
        }
        .sc-eyebrow {
          font-weight: 700;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #5b6678);
          margin-bottom: 6px;
        }
        .sc-right-sub {
          font-size: 13.5px;
          color: var(--tg-ink-2, #333);
          line-height: 1.55;
          margin: 0 0 18px;
        }
        .sc-check {
          display: flex;
          gap: 12px;
          padding: 14px;
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          cursor: pointer;
          margin-bottom: 10px;
          align-items: flex-start;
        }
        .sc-check.on { border-color: var(--tg-blue, #0080ff); background: var(--tg-blue-bg, #ebf2ff); }
        .sc-check input { position: absolute; opacity: 0; pointer-events: none; }
        .sc-check-mark {
          width: 18px;
          height: 18px;
          border-radius: 4px;
          border: 1.5px solid var(--tg-line-3, #d0d0d8);
          background: white;
          flex-shrink: 0;
          margin-top: 1px;
          position: relative;
        }
        .sc-check.on .sc-check-mark {
          background: var(--tg-blue, #0080ff);
          border-color: var(--tg-blue, #0080ff);
        }
        .sc-check.on .sc-check-mark::after {
          content: '';
          position: absolute;
          left: 5px;
          top: 1px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        .sc-check-text { font-size: 13.5px; color: var(--tg-ink, #111); line-height: 1.5; }
        .sc-promise {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 6px;
          padding: 10px 12px;
          background: rgba(0, 168, 96, 0.1);
          border-radius: 10px;
          font-size: 12.5px;
          color: var(--tg-up, #00a860);
          font-weight: 600;
        }
        .sc-promise svg { width: 15px; height: 15px; flex-shrink: 0; }
        .sc-error {
          margin-top: 12px;
          padding: 10px 14px;
          background: rgba(229, 70, 70, 0.1);
          border: 1px solid rgba(229, 70, 70, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
        .sc-legal {
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
          margin-top: 14px;
          line-height: 1.5;
        }
        .sc-legal-link {
          color: var(--tg-blue-press, #0052cc);
          font-weight: 600;
          text-decoration: none;
        }
        .sc-legal-link:hover { text-decoration: underline; }
      `}</style>
    </OnboardingShell>
  )
}
